import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Icon } from '@iconify/react';
import {
  Accordion,
  AccordionItem,
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Card,
  Input,
  ScrollShadow,
  Select,
  Selection,
  SelectItem,
  Spinner,
  Textarea,
} from '@nextui-org/react';

import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import CohortMultiSelect, {
  resolveCohortIds,
} from '@/components/cohorts/cohort-multi-select';
import { useCohorts } from '@/hooks/queries/cohorts';
import { useTagSpecs } from '@/hooks/queries/common';
import {
  useCreateLearningPath,
  useLearningPath,
  usePublishLearningPath,
  useUpdateLearningPath,
} from '@/hooks/queries/learning-paths';
import PromptService from '@/services/prompt-service';
import { useTenantStore } from '@/store/use-tenant-store';
import { PromptData, StandardResponse } from '@/types/agent';
import {
  AgentTile,
  LearningPath,
  LearningPathAgent,
  LearningPathCohortPayload,
} from '@/types/learning-path';
import { TagSpec } from '@/types/tag';
import { SortableAgentCard } from './components/sortable-agent-card';

const getLearningPathCohortIds = (
  learningPath: Pick<LearningPath, 'cohort_ids' | 'cohorts'>
): number[] => {
  if (learningPath.cohorts?.length) {
    return [...learningPath.cohorts]
      .sort((a, b) => a.order - b.order)
      .map((cohort) => cohort.cohort_id);
  }

  if (learningPath.cohort_ids?.length) {
    return learningPath.cohort_ids;
  }

  return [];
};

const buildCohortsPayload = (
  cohortIds: number[]
): LearningPathCohortPayload[] =>
  cohortIds.map((cohortId, index) => ({
    cohort_id: cohortId,
    order: index,
  }));

const LearningPathBuilder = () => {
  const navigate = useNavigate();

  const { tenantSlug, id } = useParams<{ tenantSlug: string; id: string }>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tag, setTag] = useState('');
  const [selectedTagSpecId, setSelectedTagSpecId] = useState('');
  const [selectedCohortIds, setSelectedCohortIds] = useState<Selection>(
    new Set([])
  );
  const [showCohortError, setShowCohortError] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState<AgentTile[]>([]);

  const createMutation = useCreateLearningPath();
  const updateMutation = useUpdateLearningPath(id ? parseInt(id, 10) : 0);
  const publishMutation = usePublishLearningPath();

  const { data: learningPathData, isLoading: isLoadingPath } = useLearningPath(
    id ? parseInt(id) : undefined,
    {
      enabled: !!id,
    }
  );

  const { data: tagSpecs } = useTagSpecs();
  const { data: cohorts } = useCohorts();
  const tenantConfig = useTenantStore((state) => state.config);

  const selectedTagSpec = useMemo(() => {
    if (!tagSpecs?.results || !selectedTagSpecId) return null;
    return tagSpecs.results.find(
      (spec: TagSpec) => spec.id.toString() === selectedTagSpecId
    );
  }, [tagSpecs, selectedTagSpecId]);

  // Helper function to get tab label for a tag
  const getTabLabelForTag = (tagValue: string) => {
    const tab = tenantConfig?.dashboard_config?.tabs?.find(
      (tab) => tab.tag === tagValue
    );
    return tab?.label;
  };

  const getDisplayValue = (value: string) => {
    const tabLabel = getTabLabelForTag(value);
    console.log(tabLabel);
    return tabLabel ? `${value} (${tabLabel})` : value;
  };

  const getSelectedCohortIds = () =>
    resolveCohortIds(selectedCohortIds, cohorts?.results ?? []);

  const selectedCohortIdList = useMemo(
    () => getSelectedCohortIds(),
    [selectedCohortIds, cohorts?.results]
  );

  const hasSelectedCohorts = selectedCohortIdList.length > 0;

  React.useEffect(() => {
    if (learningPathData && learningPathData?.id) {
      setName(learningPathData.name);
      setDescription(learningPathData.description);
      const currentTag = learningPathData.tag || '';
      setTag(currentTag);

      if (tagSpecs?.results && currentTag) {
        const matchingSpec = tagSpecs.results.find((spec: TagSpec) =>
          spec.constraints.allowed_values.some((v) => v.value === currentTag)
        );
        if (matchingSpec) {
          setSelectedTagSpecId(matchingSpec.id.toString());
        }
      }

      if (learningPathData.prompts) {
        setSelectedAgents(
          learningPathData.prompts.map((agent: LearningPathAgent) => ({
            id: agent.prompt.id,
            name: agent.prompt.name,
            required: agent.is_required,
            note: agent.note,
          }))
        );
      }

      const cohortIds = getLearningPathCohortIds(learningPathData);
      if (cohortIds.length > 0) {
        setSelectedCohortIds(
          new Set(cohortIds.map((cohortId) => cohortId.toString()))
        );
      }
    }
  }, [learningPathData, tagSpecs]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data: agents, isLoading } = useQuery<StandardResponse<PromptData>>({
    queryKey: [`agent-${tag}`, { is_active: true, tag }],
    queryFn: async (): Promise<StandardResponse<PromptData>> => {
      const response = await PromptService.getPrompts({
        filters: {
          page: 1,
          is_active: true,
          tag,
        },
      });
      return response as StandardResponse<PromptData>;
    },
    enabled: !!tag,
  });

  const availableAgents = useMemo(() => {
    if (!agents?.results) return [];
    const selectedAgentIds = new Set(selectedAgents.map((a) => a.id));
    return agents.results.filter((agent) => !selectedAgentIds.has(agent.id));
  }, [agents, selectedAgents]);

  const handleDragEnd = (event: {
    active: { id: any };
    over: { id: any } | null;
  }) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setSelectedAgents((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveDraft = async ({
    navigationRequired = true,
  }: { navigationRequired?: boolean } = {}): Promise<void | number> => {
    setShowCohortError(true);

    if (!hasSelectedCohorts) {
      toast.error('Please select at least one cohort.');
      return;
    }

    const promptsPayload = selectedAgents.map((agent, index) => ({
      prompt_id: agent.id,
      order: index + 1,
      is_required: agent.required,
      note: agent.note || `Step ${index + 1}`,
    }));

    const cohortsPayload = buildCohortsPayload(selectedCohortIdList);

    const handleNavigation = () => {
      navigate(`/${tenantSlug}/learning-paths`);
    };

    if (id) {
      const payload = {
        name,
        description,
        tag,
        prompts: promptsPayload,
        cohorts: cohortsPayload,
      };
      updateMutation.mutateAsync(payload as any, {
        onSuccess: () => {
          if (navigationRequired) {
            handleNavigation();
          }
        },
      });
    } else {
      if (!tag) {
        toast.error('Please enter an agent tag.');
        return;
      }
      const payload = {
        name,
        description,
        tag,
        prompts: promptsPayload,
        completion_rule: 'ALL_REQUIRED',
        cohorts: cohortsPayload,
      };
      createMutation.mutateAsync(payload as any, {
        onSuccess: (response) => {
          if (navigationRequired) {
            handleNavigation();
          } else {
            handlePublish(response.id);
          }
        },
      });
    }
  };

  const handlePublish = async (newPathId?: string | number) => {
    console.log(newPathId);
    const pathId = newPathId || id;
    if (learningPathData?.status !== 'DRAFT' && !newPathId) {
      const x = await handleSaveDraft({ navigationRequired: false });
      console.log(x);
      // await handlePublish();
      return;
    }
    if (!pathId) return;
    publishMutation.mutate(Number(pathId), {
      onSuccess: () => {
        navigate(`/${tenantSlug}/learning-paths`);
      },
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 px-2 sm:px-6 py-2 border-b border-divider bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Breadcrumbs
              size="sm"
              classNames={{
                list: 'gap-2 text-sm',
              }}
            >
              <BreadcrumbItem
                onPress={() => navigate(`/${tenantSlug}/learning-paths`)}
              >
                <span className="text-sm">Learning Path</span>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <span className="text-sm">Path Builder</span>
              </BreadcrumbItem>
            </Breadcrumbs>
          </div>
          <div className="flex gap-2">
            {learningPathData?.status !== 'PUBLISHED' && (
              <Button
                variant="bordered"
                size="sm"
                onPress={() => handleSaveDraft({ navigationRequired: true })}
                isLoading={createMutation.isPending || updateMutation.isPending}
              >
                {id ? 'Update Draft' : 'Save as Draft'}
              </Button>
            )}
            {learningPathData?.status === 'PUBLISHED' && (
              <Button
                variant="bordered"
                size="sm"
                onPress={() => handleSaveDraft({ navigationRequired: true })}
                isLoading={updateMutation.isPending}
              >
                Update Path
              </Button>
            )}
            {learningPathData?.status !== 'PUBLISHED' && (
              <Button
                color="primary"
                size="sm"
                onPress={() => handlePublish(id)}
                isLoading={publishMutation.isPending}
              >
                Publish
              </Button>
            )}
          </div>
        </div>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="min-h-0 flex-1 grid sm:grid-cols-[400px,1fr] overflow-hidden">
          <div className="min-h-0 overflow-y-auto py-4 px-0 sm:px-4 sm:border-r">
            <Accordion
              defaultExpandedKeys={['basic-info', 'agents']}
              selectionMode="single"
              className="gap-4"
            >
              <AccordionItem
                key="basic-info"
                aria-label="Basic Info"
                title="Basic Info"
                classNames={{
                  title: 'text-lg font-semibold',
                  content: 'pb-4',
                }}
              >
                <div className="space-y-4">
                  <Input
                    label="Name"
                    placeholder="Enter path name"
                    value={name}
                    onValueChange={setName}
                  />
                  <Input label="Type" value="Self-Paced" isDisabled />
                  <Select
                    label="Tag Spec"
                    selectedKeys={selectedTagSpecId ? [selectedTagSpecId] : []}
                    onSelectionChange={(keys) => {
                      const newSpecId = Array.from(keys)[0] as string;
                      if (
                        selectedTagSpecId &&
                        newSpecId !== selectedTagSpecId
                      ) {
                        setTag('');
                        setSelectedAgents([]);
                      }
                      setSelectedTagSpecId(newSpecId);
                    }}
                    isDisabled={!!id}
                  >
                    {tagSpecs?.results.map((spec: TagSpec) => (
                      <SelectItem
                        key={spec.id.toString()}
                        value={spec.id.toString()}
                      >
                        {spec.label}
                      </SelectItem>
                    )) || []}
                  </Select>
                  <Select
                    label="Agent Tag"
                    selectedKeys={tag ? [tag] : []}
                    onSelectionChange={(keys) => {
                      const newTag = Array.from(keys)[0] as string;
                      if (tag && newTag !== tag) {
                        setSelectedAgents([]);
                      }
                      setTag(newTag);
                    }}
                    isDisabled={!!id || !selectedTagSpecId}
                  >
                    {selectedTagSpec?.constraints.allowed_values
                      .filter((tagOption) => tagOption.is_active)
                      .map(
                        (
                          tagOption: TagSpec['constraints']['allowed_values'][number]
                        ) => (
                          <SelectItem
                            key={tagOption.value}
                            value={tagOption.value}
                          >
                            {getDisplayValue(tagOption.value)}
                          </SelectItem>
                        )
                      ) || []}
                  </Select>
                  <Textarea
                    label="Description"
                    placeholder="Enter path description"
                    value={description}
                    onValueChange={setDescription}
                  />
                  <CohortMultiSelect
                    selectedKeys={selectedCohortIds}
                    onSelectionChange={setSelectedCohortIds}
                    isRequired
                    isInvalid={showCohortError && !hasSelectedCohorts}
                    errorMessage={
                      showCohortError && !hasSelectedCohorts
                        ? 'At least one cohort is required'
                        : undefined
                    }
                  />
                </div>
              </AccordionItem>

              <AccordionItem
                key="agents"
                aria-label="Agents"
                title={`Agents (${availableAgents.length})`}
                classNames={{
                  title: 'text-lg font-semibold',
                  content: 'pb-4',
                }}
              >
                <div>
                  {isLoading || isLoadingPath ? (
                    <div className="flex items-center justify-center py-12">
                      <Spinner size="lg" />
                    </div>
                  ) : (
                    <div className="w-full">
                      <div className="space-y-2 pr-2 w-full mb-8">
                        {availableAgents.map((agent) => (
                          <div
                            key={agent.id}
                            className="w-full bg-default-100 rounded-medium p-3 flex items-center gap-3 cursor-pointer hover:bg-default-200"
                            onClick={() => {
                              const newAgent: AgentTile = {
                                id: agent.id,
                                name: agent.name,
                                required: true,
                              };
                              setSelectedAgents((prev) => [...prev, newAgent]);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <Icon
                                icon="lucide:plus"
                                className="text-default-400"
                              />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-sm">
                                {agent.name}
                              </h4>
                            </div>
                            {/* <Icon
                              icon="lucide:menu"
                              className="text-default-400"
                            /> */}
                          </div>
                        ))}
                        {availableAgents.length === 0 &&
                          agents?.results &&
                          agents.results.length > 0 && (
                            <div className="flex items-center justify-center py-6 sm:py-12 text-center text-default-400">
                              <div>
                                <Icon
                                  icon="lucide:check-circle-2"
                                  className="mx-auto mb-2 h-12 w-12"
                                />
                                <p>All agents have been added to the path.</p>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </AccordionItem>
            </Accordion>
          </div>
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <Card className="relative flex h-full min-h-0 flex-1 flex-col border-none rounded-none p-0 sm:p-6">
              <SortableContext
                items={selectedAgents.map((agent) => agent.id)}
                strategy={rectSortingStrategy}
              >
                <ScrollShadow
                  size={40}
                  offset={0}
                  orientation="vertical"
                  className="grid min-h-0 flex-1 auto-rows-max grid-cols-2 sm:grid-cols-3 gap-3 overflow-y-auto p-3 w-full"
                >
                  {selectedAgents.map((agent, index) => (
                    <SortableAgentCard
                      key={agent.id}
                      agent={agent}
                      index={index}
                      onRemove={(id) => {
                        setSelectedAgents((prev) =>
                          prev.filter((a) => a.id !== id)
                        );
                      }}
                    />
                  ))}
                </ScrollShadow>
              </SortableContext>
              {selectedAgents.length === 0 && (
                <div className="relative sm:absolute inset-0 flex items-center justify-center text-center text-default-400">
                  <div>
                    <Icon
                      icon="lucide:inbox"
                      className="w-12 h-12 mx-auto mb-2"
                    />
                    <p>Click on an agent to add it to your path</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </DndContext>
    </div>
  );
};

export default LearningPathBuilder;
